import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { Spinner } from '@/design-system';
import { AppLayout } from '@/app/layouts/AppLayout';
import { AuthLayout } from '@/app/layouts/AuthLayout';
import { RedirectIfAuthenticated, RequireAuth, RequireRole } from './guards';
import { ROUTES } from './routes';

const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const ProjectsHomePage = lazy(() => import('@/features/projects/pages/ProjectsHomePage'));
const ProjectsLayout = lazy(() =>
  import('@/features/projects/layouts/ProjectsLayout').then((m) => ({ default: m.ProjectsLayout })),
);
const UsersManagementPage = lazy(() => import('@/features/admin/pages/UsersManagementPage'));
const StructurePage = lazy(() => import('@/features/admin/pages/StructurePage'));
const PortfolioPage = lazy(() => import('@/features/portfolio/pages/PortfolioPage'));
const ForbiddenPage = lazy(() => import('@/features/errors/ForbiddenPage'));
const NotFoundPage = lazy(() => import('@/features/errors/NotFoundPage'));

function PageFallback() {
  return (
    <div className="flex h-64 items-center justify-center">
      <Spinner size="lg" className="text-brand-accent" />
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: <RedirectIfAuthenticated />,
    children: [
      {
        element: <AuthLayout />,
        children: [{ path: ROUTES.login, element: <LoginPage /> }],
      },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: ROUTES.home, element: <Navigate to={ROUTES.projects} replace /> },
          { path: ROUTES.projects, element: <ProjectsHomePage /> },
          { path: ROUTES.project(), element: <ProjectsLayout /> },
          {
            element: <RequireRole permission="admin.panel.view" />,
            children: [{ path: ROUTES.portfolio, element: <PortfolioPage /> }],
          },
          {
            element: <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'PM']} permission="admin.panel.view" />,
            children: [
              { path: ROUTES.adminUsers, element: <UsersManagementPage /> },
              { path: ROUTES.adminSpaces, element: <StructurePage /> },
              { path: `${ROUTES.admin}/*`, element: <UsersManagementPage /> },
            ],
          },
        ],
      },
    ],
  },
  { path: ROUTES.forbidden, element: <ForbiddenPage /> },
  { path: '*', element: <NotFoundPage /> },
]);

export function AppRouter() {
  return (
    <Suspense fallback={<PageFallback />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}
